import { Component, useEffect, useMemo, useState } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Gantt, ViewMode } from 'gantt-task-react';
import type { Task } from 'gantt-task-react';
import 'gantt-task-react/dist/index.css';
import '../styles/gantt-dark.css';
import { useProjectStore } from '../store/useProjectStore';
import { useWorkItemStore } from '../store/useWorkItemStore';
import { useAppStore } from '../store/useAppStore';
import { useConfigStore } from '../store/useConfigStore';
import { usePIStore } from '../store/usePIStore';
import { BarChart3, ChevronDown, AlertTriangle, CalendarRange } from 'lucide-react';
import RefreshButton from '../components/common/RefreshButton';

// Error boundary to catch gantt-task-react rendering errors
class GanttErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) { console.error('Gantt render error:', error, info); }
  render() {
    if (this.state.error) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-red-500 gap-3 p-8">
          <AlertTriangle size={48} />
          <p className="text-lg font-semibold">Gantt chart failed to render</p>
          <pre className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-900 p-4 rounded max-w-2xl overflow-auto">
            {this.state.error.message}
          </pre>
          <button onClick={() => this.setState({ error: null })} className="px-4 py-2 bg-accent text-white rounded-lg text-sm mt-2">Retry</button>
        </div>
      );
    }
    return this.props.children;
  }
}

type GanttViewMode = 'Day' | 'Week' | 'Month';

// Color scheme per work-item type: button, light bar background, bright progress
const TYPE_COLORS: Record<string, { btn: string; btnText: string; bar: string; barSelected: string; progress: string; progressSelected: string }> = {
  epic:    { btn: 'bg-purple-600',  btnText: 'text-white', bar: '#d8b4fe', barSelected: '#c084fc', progress: '#9333ea', progressSelected: '#7e22ce' },
  feature: { btn: 'bg-blue-600',    btnText: 'text-white', bar: '#93c5fd', barSelected: '#60a5fa', progress: '#2563eb', progressSelected: '#1d4ed8' },
  story:   { btn: 'bg-emerald-600', btnText: 'text-white', bar: '#6ee7b7', barSelected: '#34d399', progress: '#059669', progressSelected: '#047857' },
  task:    { btn: 'bg-amber-500',   btnText: 'text-white', bar: '#fcd34d', barSelected: '#fbbf24', progress: '#d97706', progressSelected: '#b45309' },
  bug:     { btn: 'bg-red-600',     btnText: 'text-white', bar: '#fca5a5', barSelected: '#f87171', progress: '#ef4444', progressSelected: '#dc2626' },
  security:{ btn: 'bg-orange-500',  btnText: 'text-white', bar: '#fdba74', barSelected: '#fb923c', progress: '#f97316', progressSelected: '#ea580c' },
};

const ALL_TYPES = ['epic', 'feature', 'story', 'task', 'bug', 'security'] as const;

// Normalize work item type (user-story → story)
const normalizeType = (t: string) => t === 'user-story' ? 'story' : t;

export default function GanttPage({ projectId: scoped }: { projectId?: string } = {}) {
  const { projects } = useProjectStore();
  const { items, fetchByProject } = useWorkItemStore();
  const { selectedProjectId: storeProjectId, setSelectedProject, darkMode } = useAppStore();
  const selectedProjectId = scoped || storeProjectId;
  const { getHealthColor } = useConfigStore();
  const { pis, getSprintsForPI, fetchPIs, fetchAllSprints } = usePIStore();
  const [viewMode, setViewMode] = useState<GanttViewMode>('Week');
  const [showProjectPicker, setShowProjectPicker] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set(ALL_TYPES));
  const [selectedPIId, setSelectedPIId] = useState<string | null>(null);
  const [showPIPicker, setShowPIPicker] = useState(false);

  const currentProject = projects.find((p) => p._id === selectedProjectId);
  const selectedPI = pis.find((p) => p._id === selectedPIId);
  const piSprints = useMemo(() => selectedPIId ? getSprintsForPI(selectedPIId) : [], [selectedPIId, getSprintsForPI]);

  const toggleType = (type: string) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        // Don't allow deselecting all
        if (next.size <= 1) return prev;
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  useEffect(() => {
    if (selectedProjectId) {
      fetchByProject(selectedProjectId);
    }
  }, [selectedProjectId, fetchByProject]);

  useEffect(() => {
    fetchPIs();
    fetchAllSprints();
  }, [fetchPIs, fetchAllSprints]);

  const ganttViewMode: ViewMode =
    viewMode === 'Day' ? ViewMode.Day :
    viewMode === 'Month' ? ViewMode.Month :
    ViewMode.Week;

  const tasks: Task[] = useMemo(() => {
    if (!currentProject) return [];

    const now = new Date();
    const result: Task[] = [];

    // Add project as a top-level milestone/project bar
    const projStartRaw = currentProject.startDate ? new Date(currentProject.startDate) : now;
    const projEndRaw = currentProject.endDate ? new Date(currentProject.endDate) : new Date(now.getTime() + 90 * 86400000);
    // Ensure start is valid, end > start
    const projStart = isNaN(projStartRaw.getTime()) ? now : projStartRaw;
    const projEnd = isNaN(projEndRaw.getTime()) || projEndRaw <= projStart
      ? new Date(projStart.getTime() + 90 * 86400000)
      : projEndRaw;

    let displayOrderCounter = 0;

    result.push({
      start: projStart,
      end: projEnd,
      name: currentProject.name,
      id: currentProject._id,
      type: 'project',
      progress: currentProject.stats?.completion || 0,
      hideChildren: false,
      displayOrder: displayOrderCounter++,
      styles: { progressColor: getHealthColor(currentProject.health), progressSelectedColor: getHealthColor(currentProject.health) },
    });

    // Add PI and Sprint bars if a PI is selected
    if (selectedPI && piSprints.length > 0) {
      const piStart = new Date(selectedPI.startDate);
      const piEnd = new Date(selectedPI.endDate);
      const piId = `pi:${selectedPI._id}`;

      result.push({
        start: isNaN(piStart.getTime()) ? now : piStart,
        end: isNaN(piEnd.getTime()) ? new Date(now.getTime() + 90 * 86400000) : piEnd,
        name: `📅 ${selectedPI.name}`,
        id: piId,
        type: 'project',
        progress: 0,
        hideChildren: false,
        project: currentProject._id,
        displayOrder: displayOrderCounter++,
        styles: {
          backgroundColor: '#6366f1',
          backgroundSelectedColor: '#4f46e5',
          progressColor: '#6366f1',
          progressSelectedColor: '#4f46e5',
        },
      });

      const sprintColors = [
        { bar: '#a5b4fc', sel: '#818cf8', prog: '#6366f1', progSel: '#4f46e5' },  // indigo
        { bar: '#93c5fd', sel: '#60a5fa', prog: '#3b82f6', progSel: '#2563eb' },  // blue
        { bar: '#67e8f9', sel: '#22d3ee', prog: '#06b6d4', progSel: '#0891b2' },  // cyan
        { bar: '#86efac', sel: '#4ade80', prog: '#22c55e', progSel: '#16a34a' },  // green
      ];

      piSprints.forEach((sprint, idx) => {
        const sStart = new Date(sprint.startDate);
        const sEnd = new Date(sprint.endDate);
        const colors = sprintColors[idx % sprintColors.length];
        const statusLabel = sprint.status === 'active' ? ' ▶' : sprint.status === 'completed' ? ' ✓' : '';

        result.push({
          start: isNaN(sStart.getTime()) ? now : sStart,
          end: isNaN(sEnd.getTime()) ? new Date(now.getTime() + 14 * 86400000) : sEnd,
          name: `${sprint.name}${statusLabel}`,
          id: `sprint:${sprint._id}`,
          type: 'task',
          progress: sprint.status === 'completed' ? 100 : sprint.status === 'active' ? 50 : 0,
          project: piId,
          displayOrder: displayOrderCounter++,
          styles: {
            backgroundColor: colors.bar,
            backgroundSelectedColor: colors.sel,
            progressColor: colors.prog,
            progressSelectedColor: colors.progSel,
          },
        });
      });
    }

    // Build a lookup of all items so we can walk up the parent chain
    const itemMap = new Map(items.map((i) => [i._id, i]));

    // Build children map for tree-walk ordering
    const childrenMap = new Map<string | null, typeof items>();
    for (const item of items) {
      const pid = item.parentId || null;
      if (!childrenMap.has(pid)) childrenMap.set(pid, []);
      childrenMap.get(pid)!.push(item);
    }
    // Sort siblings by order
    for (const children of childrenMap.values()) {
      children.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    // Depth-first tree walk to produce hierarchical order
    const orderedItems: typeof items = [];
    const walk = (parentId: string | null) => {
      const children = childrenMap.get(parentId);
      if (!children) return;
      for (const child of children) {
        orderedItems.push(child);
        walk(child._id);
      }
    };
    walk(null);
    // Include any items whose parentId doesn't resolve (orphans)
    const visited = new Set(orderedItems.map((i) => i._id));
    for (const item of items) {
      if (!visited.has(item._id)) orderedItems.push(item);
    }

    // Filter by selected types (normalize user-story → story)
    const filteredItems = orderedItems.filter((i) => selectedTypes.has(normalizeType(i.workItemType)));

    // Find nearest ancestor that maps to gantt type 'project' (epic or feature)
    const findGanttProject = (item: typeof items[0]): string => {
      let cur = item;
      const visited = new Set<string>();
      while (cur.parentId && !visited.has(cur.parentId)) {
        visited.add(cur.parentId);
        const parent = itemMap.get(cur.parentId);
        if (!parent) break;
        const pt = normalizeType(parent.workItemType);
        if (pt === 'epic' || pt === 'feature') {
          // Only reference it if it's in the filtered list
          if (selectedTypes.has(pt)) return parent._id;
          break;
        }
        cur = parent;
      }
      return currentProject._id;
    };

    // Add work items as tasks
    filteredItems.forEach((item) => {
      const startRaw = item.startDate ? new Date(item.startDate) : (item.createdAt ? new Date(item.createdAt) : now);
      const endRaw = item.dueDate ? new Date(item.dueDate) : new Date(startRaw.getTime() + 14 * 86400000);
      // Guard against Invalid Date
      const start = isNaN(startRaw.getTime()) ? now : startRaw;
      const end = isNaN(endRaw.getTime()) ? new Date(start.getTime() + 14 * 86400000) : endRaw;

      // Ensure end > start
      const safeEnd = end > start ? end : new Date(start.getTime() + 7 * 86400000);

      // Calculate progress from status.
      //
      // Defensive on purpose: this was the ONLY unguarded field access in the
      // loop — dates, `order` and `dependencies` are all already defended —
      // and it threw "Cannot read properties of undefined (reading
      // 'toLowerCase')", taking the whole Gantt down. One item without a
      // status must not blank the chart for every other item on it.
      let progress = 0;
      const st = String(item.status || '').toLowerCase();
      if (st === 'done' || st === 'completed' || st === 'closed') progress = 100;
      else if (st === 'in-progress' || st === 'in progress' || st === 'active') progress = 50;
      else if (st === 'review' || st === 'testing') progress = 75;

      // Color by work-item type (normalize user-story → story)
      const colors = TYPE_COLORS[normalizeType(item.workItemType)] || TYPE_COLORS.task;

      // Gantt type: epics/features are 'project' (collapsible groups), others are 'task'
      const isGanttProject = normalizeType(item.workItemType) === 'epic' || normalizeType(item.workItemType) === 'feature';

      // project field must reference an id with gantt type 'project', or the root project
      let ganttProjectRef: string;
      if (isGanttProject) {
        // Epic/feature: reference parent if it's also an epic/feature AND selected
        if (item.parentId) {
          const parent = itemMap.get(item.parentId);
          const pt = parent ? normalizeType(parent.workItemType) : '';
          ganttProjectRef = (pt === 'epic' || pt === 'feature') && selectedTypes.has(pt)
            ? item.parentId
            : currentProject._id;
        } else {
          ganttProjectRef = currentProject._id;
        }
      } else {
        ganttProjectRef = findGanttProject(item);
      }

      const task: Task = {
        start,
        end: safeEnd,
        name: item.displayId ? `${item.displayId} ${item.title}` : item.title,
        id: item._id,
        type: isGanttProject ? 'project' : 'task',
        progress,
        project: ganttProjectRef,
        displayOrder: displayOrderCounter++,
        dependencies: item.dependencies || [],
        styles: {
          backgroundColor: colors.bar,
          backgroundSelectedColor: colors.barSelected,
          progressColor: colors.progress,
          progressSelectedColor: colors.progressSelected,
        },
      };

      result.push(task);
    });

    // Post-validate: remove dangling dependency/project references
    const idSet = new Set(result.map((t) => t.id));
    for (const t of result) {
      if (t.dependencies) {
        t.dependencies = t.dependencies.filter((d) => idSet.has(d));
      }
      if (t.project && !idSet.has(t.project)) {
        t.project = currentProject._id;
      }
    }

    return result;
  }, [currentProject, items, selectedTypes, getHealthColor, selectedPI, piSprints]);

  // Auto-scroll to PI start date when a PI is selected
  const viewDate = useMemo(() => {
    if (selectedPI) {
      const d = new Date(selectedPI.startDate);
      return isNaN(d.getTime()) ? undefined : d;
    }
    return undefined;
  }, [selectedPI]);

  if (!selectedProjectId) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-500 gap-4">
        <BarChart3 size={48} />
        <p className="text-lg">Select a project to view its Gantt chart</p>
        <div className="flex flex-wrap gap-2 mt-4">
          {projects.map((p) => (
            <button
              key={p._id}
              onClick={() => setSelectedProject(p._id)}
              className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:border-accent/50 transition-colors"
            >
              {p.name}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center gap-3">
          <BarChart3 size={20} className="text-accent" />
          <div className="relative">
            <button
              onClick={() => setShowProjectPicker(!showProjectPicker)}
              className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100 hover:text-accent transition-colors"
            >
              {currentProject?.name || 'Select Project'}
              <ChevronDown size={16} />
            </button>
            {showProjectPicker && (
              <div className="absolute left-0 top-full mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20">
                {projects.map((p) => (
                  <button
                    key={p._id}
                    onClick={() => { setSelectedProject(p._id); setShowProjectPicker(false); }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* PI Selector */}
          <div className="relative">
            <button
              onClick={() => setShowPIPicker(!showPIPicker)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm hover:border-accent/50 transition-colors"
            >
              <CalendarRange size={14} className="text-accent" />
              <span className="text-gray-700 dark:text-gray-200 truncate max-w-[120px]">
                {selectedPI ? selectedPI.name : 'No PI'}
              </span>
              <ChevronDown size={14} className="text-gray-400" />
            </button>
            {showPIPicker && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowPIPicker(false)} />
                <div className="absolute right-0 top-full mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-40 max-h-64 overflow-y-auto">
                  <button
                    onClick={() => { setSelectedPIId(null); setShowPIPicker(false); }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                      !selectedPIId ? 'text-accent font-semibold bg-accent/5' : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    No PI overlay
                  </button>
                  {pis.map((pi) => (
                    <button
                      key={pi._id}
                      onClick={() => { setSelectedPIId(pi._id); setShowPIPicker(false); }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                        selectedPIId === pi._id ? 'text-accent font-semibold bg-accent/5' : 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {pi.name}
                      <span className="text-[10px] text-gray-400 ml-2">{pi.startDate} → {pi.endDate}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Type filter - multi-select toggle */}
          <div className="flex items-center gap-1.5 text-sm">
            {ALL_TYPES.map((t) => {
              const colors = TYPE_COLORS[t];
              const active = selectedTypes.has(t);
              return (
                <button
                  key={t}
                  onClick={() => toggleType(t)}
                  className={`px-2.5 py-1 rounded text-xs font-medium capitalize transition-all ${
                    active
                      ? `${colors.btn} ${colors.btnText} shadow-sm`
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 opacity-60'
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>

          <RefreshButton
            onRefresh={async () => {
              if (selectedProjectId) await fetchByProject(selectedProjectId);
              await Promise.all([fetchPIs(), fetchAllSprints()]);
            }}
            tooltip="Refresh Gantt data"
          />

          {/* View mode */}
          <div className="flex border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
            {(['Day', 'Week', 'Month'] as GanttViewMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-3 py-1.5 text-xs font-medium ${
                  viewMode === mode
                    ? 'bg-accent text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {mode}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Gantt Chart */}
      <div className="flex-1 overflow-auto bg-white dark:bg-gray-800 p-4">
        {tasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <BarChart3 size={48} className="mb-3" />
            <p>No items with dates found</p>
            <p className="text-sm mt-1">Add due dates to your work items to see them on the Gantt chart</p>
          </div>
        ) : (
          <GanttErrorBoundary>
          <Gantt
            tasks={tasks}
            viewMode={ganttViewMode}
            viewDate={viewDate}
            listCellWidth=""
            columnWidth={viewMode === 'Day' ? 60 : viewMode === 'Week' ? 100 : 300}
            barFill={60}
            fontSize="12"
            headerHeight={50}
            rowHeight={40}
            todayColor={darkMode ? 'rgba(99, 102, 241, 0.15)' : 'rgba(99, 102, 241, 0.08)'}
            barBackgroundColor={darkMode ? '#4b5563' : '#a3a3f5'}
            barBackgroundSelectedColor={darkMode ? '#6366f1' : '#8485f0'}
            barProgressColor={darkMode ? '#818cf8' : '#6366f1'}
            barProgressSelectedColor={darkMode ? '#a5b4fc' : '#4f46e5'}
            projectProgressColor={darkMode ? '#34d399' : '#10b981'}
            projectProgressSelectedColor={darkMode ? '#6ee7b7' : '#059669'}
            projectBackgroundColor={darkMode ? '#374151' : '#e5e7eb'}
            projectBackgroundSelectedColor={darkMode ? '#4b5563' : '#d1d5db'}
            arrowColor={darkMode ? '#6b7280' : '#9ca3af'}
          />
          </GanttErrorBoundary>
        )}
      </div>
    </div>
  );
}
